/**
 * Readers for GET /cloudnova/audit/hub/audits/{auditId}.
 * Scores are passed / (passed + failed), a ratio in [0, 1] (or already 0–100).
 * Not-applicable and undecided controls are outside that ratio.
 */

const asCount = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return n;
};

export const EMPTY_PROGRESS = { total: 0, decided: 0, inReview: 0, outstanding: 0 };

export const EMPTY_COMPONENTS = {
  autoPassed: 0,
  autoFailed: 0,
  manualPassed: 0,
  manualFailed: 0,
  notApplicable: 0,
  undecided: 0,
};

export const readProgress = (progress) => ({
  total: asCount(progress?.total),
  decided: asCount(progress?.decided),
  inReview: asCount(progress?.inReview),
  outstanding: asCount(progress?.outstanding),
});

export const addProgress = (left, right) => ({
  total: left.total + right.total,
  decided: left.decided + right.decided,
  inReview: left.inReview + right.inReview,
  outstanding: left.outstanding + right.outstanding,
});

export const readComponents = (components) => ({
  autoPassed: asCount(components?.passedControls),
  autoFailed: asCount(components?.failedControls),
  manualPassed: asCount(components?.manualPassed),
  manualFailed: asCount(components?.manualFailed),
  notApplicable: asCount(components?.manualNotApplicable),
  undecided: asCount(components?.manualUndecided),
});

export const addComponents = (left, right) => ({
  autoPassed: left.autoPassed + right.autoPassed,
  autoFailed: left.autoFailed + right.autoFailed,
  manualPassed: left.manualPassed + right.manualPassed,
  manualFailed: left.manualFailed + right.manualFailed,
  notApplicable: left.notApplicable + right.notApplicable,
  undecided: left.undecided + right.undecided,
});

export const componentTotal = (components) =>
  components.autoPassed +
  components.autoFailed +
  components.manualPassed +
  components.manualFailed +
  components.notApplicable +
  components.undecided;

/** Passed / (passed + failed) on a 0–100 scale. Null when nothing is scoreable. */
export const ratioPercent = (passed, failed) =>
  passed + failed > 0 ? (passed / (passed + failed)) * 100 : null;

/** Assessment score from automated and manual pass / fail counts. */
export const assessedFromComponents = (components) =>
  ratioPercent(components.autoPassed + components.manualPassed, components.autoFailed + components.manualFailed);

/**
 * Compliance Score card.
 * Total = (automated total + manual total) − N/A.
 * Undecided = manual total − (manual passed + manual failed + manual N/A).
 * Score = passed / total. N/A is not part of either number.
 */
export const complianceScoreParts = (components) => {
  const automatedTotal = components.autoPassed + components.autoFailed;
  const manualPassed = components.manualPassed;
  const manualFailed = components.manualFailed;
  const notApplicable = components.notApplicable;
  const manualTotal = manualPassed + manualFailed + notApplicable + components.undecided;
  const undecided = Math.max(0, manualTotal - (manualPassed + manualFailed + notApplicable));
  const total = Math.max(0, automatedTotal + manualTotal - notApplicable);
  const passed = components.autoPassed + manualPassed;
  const failed = components.autoFailed + manualFailed;
  return {
    total,
    passed,
    failed,
    notApplicable,
    undecided,
    score: total > 0 ? (passed / total) * 100 : null,
  };
};

/** One account's compliance score from its scope component counts. Null when nothing is scoreable. */
export const scopeComplianceScore = (components) => {
  const score = complianceScoreParts(readComponents(components)).score;
  return Number.isFinite(score) ? score : null;
};

/** Automated score from scan pass / fail counts, before manual decisions. */
export const baselineFromComponents = (components) =>
  ratioPercent(components.autoPassed, components.autoFailed);
